// FIRST, and out of order on purpose: importing this module installs the
// `infra.error.unhandled` listeners as a side effect, and ES imports evaluate
// in order — anything imported above it could throw while evaluating before a
// listener exists. See the header of ./logging/earlyErrorCapture.ts, and
// logging/earlyImportWall.test.ts, which fails if this stops being first.
import "./logging/earlyErrorCapture";
import { StrictMode } from "react";
import { ConvexReactClient } from "convex/react";
import "./index.css";
import App from "./App";
import { AppProviders } from "@/app-root";
import { env } from "@/env";
import { consumeAuthErrorParam, wireConvexAuth } from "@/lib/auth";
import { mountOrHydrate } from "@bindery/prerender/mount";
import { watchInstallability } from "@bindery/ui/lib/install-prompt";
import { installState } from "./install/store";
import { registerServiceWorker } from "./registerServiceWorker";

// Captured here, before the app renders, so it survives even if the matched
// route (`/auth/callback`) immediately navigates the URL elsewhere — App
// surfaces it as a toast.
const authError = consumeAuthErrorParam();

// Before hydration, not in a component: Chromium fires `beforeinstallprompt`
// once and early, and a listener attached in an effect usually arrives after
// it. The install offer (src/install/InstallOffer.tsx) reads the handle this
// keeps. See @bindery/ui/lib/install-prompt.
watchInstallability(installState);

const convex = new ConvexReactClient(env.VITE_CONVEX_URL);
// Feed Convex the cookie-backed session token (in-memory only; anonymous when
// absent). See src/lib/auth.ts + docs/plan/08-auth-worker.md.
// Deliberately plain ConvexProvider + manual setAuth, not
// ConvexProviderWithAuth (guidelines "Authentication") — spec-endorsed by
// plan 08 §4.
wireConvexAuth(convex);

const container = document.getElementById("root")!;

// The provider stack lives in @/app-root because scripts/prerender.tsx mounts
// the same one; only the Convex client differs between them.
const tree = (
  <StrictMode>
    <AppProviders client={convex}>
      <App authError={authError} />
    </AppProviders>
  </StrictMode>
);

// Hydrate when this document was prerendered for THIS path, mount from
// scratch otherwise: the routes in src/prerender-manifest.ts arrive with a
// prerendered document and every other path is served the SPA fallback's
// shell, so both cases exist in one build. @bindery/prerender/mount has the
// reasoning, including why the check is a path and not "has children".
mountOrHydrate(container, tree);

// Production only, and on `load`, after the page has had the network to
// itself — see ./registerServiceWorker.ts.
registerServiceWorker();
